import { useState, useEffect, useMemo } from "react";
import { Button } from "primereact/button";
import { Timeline } from "primereact/timeline";
import { Dropdown } from "primereact/dropdown";
import C from "classnames";

import { useMediaQuery, MEDIA_QUERY_BELOW_XL } from "sj-ui-toolkit/src/hooks";
import { getCalendarDiff } from "sj-ui-toolkit/src/utils";
import { ProcessedSubmission, TestResult } from "sj-ui-toolkit/src/api/types";

import { useSandbox } from "../../../contexts/SandboxHooks";
import { getSubmissionExplanation, getResultText } from "../../../contexts/Submissions";

import CodeViewModal from "./CodeViewModal";
import AiHintButton from "./AiHintButton";

import S from "./Tests.module.scss";

const Tests = () => {
  const { lessonDetails, aiHintsEnabled, submissions, newSubmission } = useSandbox();
  const [selectedSubmission, setSelectedSubmission] = useState<ProcessedSubmission | null>(null);
  const [codeModalVisible, setCodeModalVisible] = useState(false);
  const isStacked = useMediaQuery(MEDIA_QUERY_BELOW_XL);

  useEffect(() => {
    if (submissions.length > 0 && !selectedSubmission) {
      setSelectedSubmission(submissions[0] ?? null);
    }
  }, [submissions, selectedSubmission]);

  // Auto-select new submission when it arrives
  useEffect(() => {
    if (newSubmission) {
      setSelectedSubmission(newSubmission);
    }
  }, [newSubmission]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "passed":
        return "pi pi-sj-test-pass";
      case "failed":
      case "error":
        return "pi pi-sj-test-fail";
      case "timeout":
        return "pi pi-spinner-dotted";
      default:
        return "pi pi-sj-circle";
    }
  };

  const getTestStatusIcon = (testStatus: string) => {
    switch (testStatus) {
      case "passed":
        return "pi-sj-test-pass";
      case "failed":
      case "error":
        return "pi-sj-test-fail";
      case "timeout":
        return "pi-spinner-dotted";
      default:
        return "pi-sj-circle";
    }
  };

  const customizedMarker = (test: TestResult) => {
    return (
      <span className={S.testTimelineMarker}>
        <i className={C("pi", getTestStatusIcon(test.status))}></i>
      </span>
    );
  };

  // `failureCode` is returned per-test at runtime but isn't on the toolkit TestResult type yet.
  const customizedContent = (test: TestResult & { failureCode?: string | null }) => {
    return (
      <div className="mb-4">
        <div className={S.testName}>
          <span>{test.title}</span>
          {test.helpUrl && test.status === "failed" && (
            <Button
              label="More info"
              icon="pi pi-external-link"
              iconPos="right"
              size="small"
              className="p-0"
              link
              onClick={() => handleHelpLinkClick(test.helpUrl)}
            />
          )}
        </div>

        <p className={C("body-sm text-muted-aaa", S.testDescription)}>{test.description}</p>

        {test.failureCode && test.failureCode.startsWith("[show]") && (
          <p className={C("body-sm text-muted-aaa", S.failureCode)}>
            {test.failureCode?.replace("[show] ", "")}
          </p>
        )}
      </div>
    );
  };

  const handleSubmissionSelect = (submission: ProcessedSubmission) => {
    setSelectedSubmission(submission);
  };

  const handleViewCode = () => {
    setCodeModalVisible(true);
  };

  const handleHelpLinkClick = (url?: string) => {
    if (!url) return;
    window.open(url, "_blank", "noopener noreferrer");
  };

  const currentSubmission = selectedSubmission || submissions[0];

  const submissionOptions = useMemo(
    () =>
      submissions.map(submission => ({
        ...submission,
        label: formatTimeAgo(submission.submittedAt),
      })),
    [submissions]
  );

  // The sandbox context types lessonDetails as SandboxLessonDetails, but the object supplied
  // (via useLessonLoader) also carries the Rails `type` field. TODO(T13): widen the context type.
  const lessonType = (lessonDetails as { type?: string } | null)?.type;
  const doShowHintButton =
    aiHintsEnabled &&
    currentSubmission?.result.type === "failed" &&
    lessonDetails?.hasCodeTests &&
    lessonType === "Break/Fix";

  return (
    <div className={S.testsWrapper}>
      {submissions.length === 0 ? (
        <div className={S.testsEmpty}>
          <p>No tests available. Click "Run Tests" to execute.</p>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {isStacked && (
            <div className={S.mobileSelect}>
              <Dropdown
                options={submissionOptions}
                optionLabel="label"
                dataKey="runId"
                filter
                filterBy="label"
                itemTemplate={submission => (
                  <span className="flex items-center gap-2">
                    <i className={getStatusIcon(submission.result.type)}></i>
                    {submission.label}
                  </span>
                )}
                valueTemplate={submission =>
                  submission ? (
                    <span className="flex items-center gap-2">
                      <i className={getStatusIcon(submission.result.type)}></i>
                      {formatTimeAgo(submission.submittedAt)}
                    </span>
                  ) : (
                    <span>Select a submission</span>
                  )
                }
                value={currentSubmission}
                onChange={e => handleSubmissionSelect(e.value)}
                placeholder="Select a submission"
                aria-label="Select a submission"
                data-testid="tests-mobile-select"
                className="w-full"
              />
            </div>
          )}
          <div className="flex flex-1 min-h-0">
            {/* Left Panel: Submissions List */}
            {!isStacked && (
              <div className={S.submissionsListPanel}>
                <div className={S.submissionsList}>
                  {submissions.map(submission => (
                    <button
                      key={submission.runId}
                      className={C(
                        S.submissionItem,
                        selectedSubmission?.runId === submission.runId && S.active
                      )}
                      data-testid={`test-submission-${submission.runId}`}
                      onClick={() => handleSubmissionSelect(submission)}
                      aria-current={selectedSubmission?.runId === submission.runId || undefined}
                    >
                      <i className={getStatusIcon(submission.result.type)}></i>
                      <span className="body-sm whitespace-nowrap">
                        {formatTimeAgo(submission.submittedAt)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Right Panel: Submission Details */}
            <div className={C(!isStacked && "w-3/4", S.submissionDetailsPanel)}>
              {currentSubmission ? (
                <div className={S.submissionDetails}>
                  <div className={S.submissionHeader}>
                    <div className={S.submissionStatus}>
                      <div className={S.statusText}>
                        <h3
                          data-testid="submission-status-heading"
                          data-result={currentSubmission.result.type}
                        >
                          {getResultText(currentSubmission.result)}
                        </h3>
                        <p className={C("body-sm text-muted-aaa", S.submissionTime)}>
                          Submitted {formatTimeAgo(currentSubmission.submittedAt, true)}
                        </p>
                      </div>
                    </div>

                    <div className={S.submissionActions}>
                      {currentSubmission.code && (
                        <Button
                          label={`View ${currentSubmission.code.languageLabel} code`}
                          icon="pi pi-code"
                          outlined
                          plain
                          size="small"
                          onClick={handleViewCode}
                        />
                      )}
                      {doShowHintButton && <AiHintButton submission={currentSubmission} />}
                    </div>
                  </div>

                  <p className={S.submissionMessage}>
                    {getSubmissionExplanation(currentSubmission.result)}
                  </p>

                  {/* Handle different submission states for timeline */}
                  {currentSubmission.result.type === "incomplete" ? (
                    <Timeline
                      value={currentSubmission.tests}
                      align="left"
                      layout="vertical"
                      className={S.testTimeline}
                      marker={() => (
                        <span
                          className={C(S.testTimelineMarker, "flex items-center justify-center")}
                        >
                          <i className={C("pi animate-spin", "pi-spinner-dotted")}></i>
                        </span>
                      )}
                      content={customizedContent}
                      pt={{
                        opposite: { className: "hidden" },
                        connector: { className: S.testTimelineConnector },
                      }}
                    />
                  ) : currentSubmission.result.type === "timeout" ||
                    currentSubmission.result.type === "error" ? null : (
                    <Timeline
                      value={currentSubmission.tests}
                      align="left"
                      layout="vertical"
                      className={S.testTimeline}
                      marker={customizedMarker}
                      content={customizedContent}
                      pt={{
                        opposite: { className: "hidden" },
                        connector: { className: S.testTimelineConnector },
                      }}
                    />
                  )}
                </div>
              ) : (
                <div className={S.submissionEmpty}>
                  <p>Select a submission to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Code View Modal */}
      <CodeViewModal
        visible={codeModalVisible}
        onHide={() => setCodeModalVisible(false)}
        code={currentSubmission?.code?.text}
        languageLabel={currentSubmission?.code?.languageLabel}
        submittedAt={currentSubmission?.submittedAt}
      />
    </div>
  );
};

const formatTimeAgo = (date: Date, lowercase = false) => {
  if (!date) return "just now";
  const now = new Date();
  const submissionDate = new Date(date);

  const { weeks, days } = getCalendarDiff(now, submissionDate);

  // If more than a week ago, show full date
  if (weeks > 0) {
    return submissionDate.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }

  let returnString = "";
  if (days > 1) {
    returnString = `${days} day${days > 1 ? "s" : ""} ago ${timeAt(submissionDate)}`;
  } else if (days === 1) {
    returnString = `Yesterday ${timeAt(submissionDate)}`;
  } else {
    const diffMins = Math.floor((now.getTime() - submissionDate.getTime()) / 60000);
    if (diffMins < 1) {
      returnString = "Just now";
    } else {
      returnString = `Today at ${timeAt(submissionDate)}`;
    }
  }
  return lowercase ? returnString[0]!.toLowerCase() + returnString.slice(1) : returnString;
};

const timeAt = (date: Date) => {
  return `at ${date.toLocaleTimeString(undefined, { timeStyle: "short" })}`; //[], { hour: "2-digit", minute: "2-digit" })}`
};

export default Tests;
